import ReactMarkdown from 'react-markdown'

type Props = {
  markdown: string
}

/**
 * Article body renderer. Sibling of `LegalMarkdown` rather than a shared
 * component: legal pages are dense running prose, articles carry images, pull
 * quotes and tables, and the two want different vertical rhythm.
 */
export function ArticleMarkdown({ markdown }: Props) {
  return (
    <div className="article-markdown">
      <ReactMarkdown
        components={{
          // The page renders the post title from frontmatter, so a body `#`
          // steps down to h2 — one h1 per document.
          h1: ({ children }) => <h2 className="h3 mt-5 mb-3">{children}</h2>,
          h2: ({ children }) => <h2 className="h3 mt-5 mb-3">{children}</h2>,
          h3: ({ children }) => <h3 className="h5 mt-4 mb-2">{children}</h3>,
          h4: ({ children }) => <h4 className="h6 mt-3 mb-2 text-body-secondary">{children}</h4>,
          p: ({ children }) => <p className="mb-3 lh-lg">{children}</p>,
          ul: ({ children }) => <ul className="mb-3 ps-4">{children}</ul>,
          ol: ({ children }) => <ol className="mb-3 ps-4">{children}</ol>,
          li: ({ children }) => <li className="mb-2 lh-lg">{children}</li>,
          a: ({ href, children }) => {
            const external = !!href && !href.startsWith('/') && !href.startsWith('#')
            return (
              <a
                href={href}
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {children}
              </a>
            )
          },
          strong: ({ children }) => <strong className="fw-semibold">{children}</strong>,
          blockquote: ({ children }) => (
            <blockquote className="article-quote my-4 ps-4">{children}</blockquote>
          ),
          img: ({ src, alt }) => (
            <img src={typeof src === 'string' ? src : undefined} alt={alt ?? ''} className="img-fluid rounded my-4" loading="lazy" />
          ),
          hr: () => <hr className="my-5" />,
          // No table override: react-markdown parses CommonMark only, so GFM
          // pipe tables never reach a renderer. Adding `remark-gfm` is the fix
          // if a post needs one.
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  )
}
